import { updateDataSettings } from '@wix/ambassador-data-v1-data-settings/http';
import { translate } from '#packages/i18n';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { CMS as CMS_APP_DEF_ID, DATA_BINDING } from '@wix/app-definition-ids';
import Constants from '#packages/constants';
import type { EditorAPI } from '@/editorAPI';
import { HttpClient } from '@wix/http-client';
import type { CompRef } from 'types/documentServices';
import type { DatabindingPublicApi } from '@wix/wix-data-client-types';
import { FieldType } from '@wix/wix-data-schema-types';
import constants from '@/constants';
import { silentInstallApps } from '@/platform';
import { ServiceSectionIds } from '../types';

const installCMS = async (sectionRef: CompRef, editorAPI: EditorAPI) => {
  const appDependencies = [CMS_APP_DEF_ID];
  const neededApps = appDependencies.filter(
    (appId) => !editorAPI.platform.isAppActive(appId),
  );

  editorAPI.selection.deselectComponents();

  if (neededApps.includes(CMS_APP_DEF_ID)) {
    await new Promise((resolve) => {
      editorAPI.wixCode.provision({
        onSuccess: () => {
          const { applicationId } =
            editorAPI.platform.getAppDataByAppDefId(DATA_BINDING);

          editorAPI.platform.notifyApplication(applicationId, {
            eventType: 'enableDataModeClicked',
            eventPayload: { origin: '[insert here some new origin]' },
          });

          resolve(true);
        },
        onError: (error) => {
          ErrorReporter.captureException(error, {
            tags: { failedToInstallCMS: true },
          });
        },
      });
    });
  }

  if (neededApps.length) {
    await silentInstallApps(editorAPI, neededApps);
  }
};

const connectRepeaterToCMS = async (
  sectionRef: CompRef,
  editorAPI: EditorAPI,
  serviceId: ServiceSectionIds,
) => {
  const [repeaterRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.REPEATER,
    sectionRef,
  );

  if (!repeaterRef) {
    return;
  }
  const pageRef = editorAPI.pages.getCurrentPage();

  const databindingPublicApi = (await editorAPI.dsRead.platform.getAppPublicApi(
    DATA_BINDING,
  )) as unknown as DatabindingPublicApi;

  const { controllerRef: datasetRef } =
    await databindingPublicApi.createDataset(pageRef, {
      displayName: translate('Dataset_DisplayName_InquiryService'),
      collectionId: 'Services/InquiryService',
    });

  // repeater connection
  await databindingPublicApi.connectBindingSection({
    componentRef: repeaterRef,
    controllerRef: datasetRef,
    newBindings: [],
    role: 'repeaterRole',
    requiresPrimaryConnection: true,
  });

  switch (serviceId) {
    case ServiceSectionIds.SERVICE_1: {
      await connectService1ToCMS(
        repeaterRef,
        datasetRef,
        editorAPI,
        databindingPublicApi,
      );
      break;
    }
    case ServiceSectionIds.SERVICE_2: {
      await connectService2ToCMS(
        repeaterRef,
        datasetRef,
        editorAPI,
        databindingPublicApi,
      );
      break;
    }
  }
};

const connectService1ToCMS = async (
  repeaterRef: CompRef,
  datasetRef: CompRef,
  editorAPI: EditorAPI,
  databindingPublicApi: DatabindingPublicApi,
) => {
  // text connection
  const [textRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.TEXT,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: textRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'name',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // image connection
  const [imageRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.PHOTO,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: imageRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'image',
        fieldType: FieldType.image,
        prop: 'src',
      },
      {
        type: 'Field',
        fieldPath: 'altText',
        fieldType: FieldType.text,
        prop: 'alt',
      },
    ],
    role: 'imageRole',
    requiresPrimaryConnection: true,
  });
};

const connectService2ToCMS = async (
  repeaterRef: CompRef,
  datasetRef: CompRef,
  editorAPI: EditorAPI,
  databindingPublicApi: DatabindingPublicApi,
) => {
  // text connection
  const [descriptionRef, textRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.TEXT,
    repeaterRef,
  );

  // service name connection
  await databindingPublicApi.connectBindingSection({
    componentRef: textRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'name',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // description name connection
  await databindingPublicApi.connectBindingSection({
    componentRef: descriptionRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'description',
        fieldType: FieldType.text,
        prop: '$text',
      },
    ],
    role: 'textRole',
    requiresPrimaryConnection: true,
  });

  // image connection
  const [imageRef] = editorAPI.components.get.byType(
    Constants.COMP_TYPES.PHOTO,
    repeaterRef,
  );

  await databindingPublicApi.connectBindingSection({
    componentRef: imageRef,
    controllerRef: datasetRef,
    newBindings: [
      {
        type: 'Field',
        fieldPath: 'image',
        fieldType: FieldType.image,
        prop: 'src',
      },
      {
        type: 'Field',
        fieldPath: 'altText',
        fieldType: FieldType.text,
        prop: 'alt',
      },
    ],
    role: 'imageRole',
    requiresPrimaryConnection: true,
  });
};

export const enableCmsForWixApps = async (editorAPI: EditorAPI) => {
  const httpClient = new HttpClient({
    getAppToken: () =>
      editorAPI.dsRead.platform.getAppDataByApplicationId(
        constants.APPLICATIONS.META_SITE_APPLICATION_ID,
      )?.instance || '',
  });

  await httpClient.request(
    updateDataSettings({
      dataSettings: {
        appCollectionsVisible: true,
      },
    }),
  );
};

export const wixServicesInstallAndConnectToCMS = async (
  sectionRef: CompRef,
  editorAPI: EditorAPI,
  serviceId: ServiceSectionIds,
): Promise<void> => {
  try {
    editorAPI.panelHelpers.openProgressBar(
      {
        title: translate('Bookings_ConnectCMS_Loader_Title'),
        totalSteps: 1,
        currentStep: 0,
        taskDisplayName: translate('Bookings_ConnectCMS_Loader_Text1'),
      },
      true,
    );
  } catch (error) {
    console.warn('Failed to open progress bar', error);
  }

  await installCMS(sectionRef, editorAPI);
  await enableCmsForWixApps(editorAPI);
  await editorAPI.waitForChangesAppliedAsync();
  await connectRepeaterToCMS(sectionRef, editorAPI, serviceId);
  await editorAPI.waitForChangesAppliedAsync();

  // @ts-expect-error - refresh method exists but is not properly typed
  await editorAPI.dsActions.pages.refresh();
  await editorAPI.waitForChangesAppliedAsync();

  try {
    await editorAPI.panelHelpers.updateProgressBar(
      1,
      translate('Bookings_ConnectCMS_Loader_Text2'),
    );
    await editorAPI.panelHelpers.closeProgressBar();
  } catch (error) {
    console.warn('Failed to update or close progress bar', error);
  }
};
